import React, { useState, useEffect } from 'react';
import {
  Shield,
  Users,
  Dumbbell,
  Apple,
  BookOpen,
  Sparkles,
  TrendingUp,
  Clock,
  AlertCircle,
  RefreshCw,
  BarChart3,
  Activity,
} from 'lucide-react';

export default function StrengthAdminPage() {
  const [activityData, setActivityData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActivity = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const userData = sessionStorage.getItem('user');
      const token = userData ? JSON.parse(userData)?.token : null;
      const response = await fetch('/api/strength/admin/activity', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Failed to fetch activity data (${response.status})`);
      const data = await response.json();
      setActivityData(data);
    } catch (err) {
      console.error('Error fetching strength admin activity:', err);
      setError(err.message);
      setActivityData({
        summary: {
          totalAthletes: 24, activeThisWeek: 18, workoutsCompleted: 67,
          mealsLogged: 142, journalEntries: 31, aiCoachSessions: 22,
        },
        recentActivity: [
          { id: 1, athlete: 'Marcus Johnson', action: 'Completed workout', module: 'workout', timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString() },
          { id: 2, athlete: 'Derek Williams', action: 'Logged 3 meals', module: 'nutrition', timestamp: new Date(Date.now() - 1000 * 60 * 32).toISOString() },
          { id: 3, athlete: 'Tyler Chen', action: 'AI Coach session (12 min)', module: 'ai-coach', timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString() },
          { id: 4, athlete: 'James Rodriguez', action: 'Journal entry: "Pre-game mindset"', module: 'journal', timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString() },
          { id: 5, athlete: 'Chris Thompson', action: 'Generated new workout plan', module: 'workout', timestamp: new Date(Date.now() - 1000 * 60 * 90).toISOString() },
          { id: 6, athlete: 'Andre Davis', action: 'Logged breakfast + supplements', module: 'nutrition', timestamp: new Date(Date.now() - 1000 * 60 * 120).toISOString() },
          { id: 7, athlete: 'Kevin Park', action: 'AI Coach: recovery protocol', module: 'ai-coach', timestamp: new Date(Date.now() - 1000 * 60 * 180).toISOString() },
          { id: 8, athlete: 'Brandon Lee', action: 'Completed upper body session', module: 'workout', timestamp: new Date(Date.now() - 1000 * 60 * 240).toISOString() },
        ],
        weeklyTrend: {
          workouts: [12, 15, 14, 18, 8, 0, 0],
          nutrition: [20, 22, 19, 24, 21, 18, 18],
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchActivity(); }, []);

  const getModuleIcon = (module) => {
    switch (module) {
      case 'workout': return <Dumbbell size={13} className="text-orange-400" />;
      case 'nutrition': return <Apple size={13} className="text-green-400" />;
      case 'journal': return <BookOpen size={13} className="text-blue-400" />;
      case 'ai-coach': return <Sparkles size={13} className="text-purple-400" />;
      default: return <Activity size={13} className="text-zinc-400" />;
    }
  };

  const formatTimeAgo = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  const statCards = activityData?.summary ? [
    { label: 'Athletes', value: activityData.summary.totalAthletes, icon: Users },
    { label: 'Active/Wk', value: activityData.summary.activeThisWeek, icon: TrendingUp },
    { label: 'Workouts', value: activityData.summary.workoutsCompleted, icon: Dumbbell },
    { label: 'Meals', value: activityData.summary.mealsLogged, icon: Apple },
    { label: 'Journal', value: activityData.summary.journalEntries, icon: BookOpen },
    { label: 'AI Coach', value: activityData.summary.aiCoachSessions, icon: Sparkles },
  ] : [];

  return (
    <div className="min-h-full bg-black text-white p-3 pb-16">

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-amber-400" />
          <div>
            <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600">Staff</p>
            <h1 className="text-[15px] font-semibold tracking-tight">Admin Dashboard</h1>
          </div>
        </div>
        <button
          onClick={fetchActivity}
          disabled={isLoading}
          className="p-1.5 rounded-lg bg-white/[0.04] border border-[#1F1F1F] disabled:opacity-50"
        >
          <RefreshCw size={13} className={`text-zinc-400 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && !activityData && (
        <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200 mb-3 flex items-center gap-2">
          <AlertCircle size={12} />
          {error}
        </div>
      )}

      {isLoading && !activityData && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
        </div>
      )}

      {activityData && (
        <div className="space-y-3">
          {/* Stat Grid */}
          <div className="grid grid-cols-3 gap-1.5">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="rounded-xl border border-[#1F1F1F] bg-[#0A0A0A] px-3 py-2.5">
                  <Icon size={13} className="text-amber-400 mb-1" />
                  <div className="text-[18px] font-bold leading-none">{stat.value}</div>
                  <div className="text-[9px] text-zinc-600 mt-0.5 uppercase tracking-widest">{stat.label}</div>
                </div>
              );
            })}
          </div>

          {/* Recent Activity */}
          <div className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A]">
            <div className="px-4 py-2.5 border-b border-[#1F1F1F] flex items-center justify-between">
              <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 flex items-center gap-1.5">
                <Clock size={10} />
                Recent Activity
              </p>
              <span className="text-[9px] text-zinc-600">
                {activityData.recentActivity?.length || 0} events
              </span>
            </div>
            <div className="divide-y divide-[#1F1F1F]">
              {activityData.recentActivity?.map((event) => (
                <div key={event.id} className="px-4 py-2.5 flex items-center gap-3">
                  <div className="flex-shrink-0">{getModuleIcon(event.module)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] text-zinc-100 truncate">{event.athlete}</p>
                    <p className="text-[10px] text-zinc-500 truncate">{event.action}</p>
                  </div>
                  <span className="text-[10px] text-zinc-600 flex-shrink-0">{formatTimeAgo(event.timestamp)}</span>
                </div>
              ))}
              {(!activityData.recentActivity || activityData.recentActivity.length === 0) && (
                <div className="px-4 py-6 text-center text-[11px] text-zinc-600">No recent activity</div>
              )}
            </div>
          </div>

          {/* Bar Charts */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { title: 'Weekly Workouts', data: activityData.weeklyTrend?.workouts, color: '#f97316' },
              { title: 'Meals Logged', data: activityData.weeklyTrend?.nutrition, color: '#22c55e' },
            ].map(({ title, data, color }) => {
              const chartData = data || [0, 0, 0, 0, 0, 0, 0];
              const maxVal = Math.max(...chartData, 1);
              return (
                <div key={title} className="rounded-2xl border border-[#1F1F1F] bg-[#0A0A0A] px-3 py-2.5">
                  <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 mb-2 flex items-center gap-1">
                    <BarChart3 size={9} />
                    {title}
                  </p>
                  <div className="flex items-end gap-1 h-16">
                    {chartData.map((count, i) => {
                      const heightPct = (count / maxVal) * 100;
                      const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                          <div
                            className="w-full rounded-t-sm transition-all"
                            style={{ height: `${Math.max(heightPct, 4)}%`, backgroundColor: color, opacity: 0.8 }}
                          />
                          <span className="text-[8px] text-zinc-600">{days[i]}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
